import React, { Fragment, useState, useRef, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import qs from 'qs';
import moment from 'moment';
import PropTypes from 'prop-types';
import {
  has,
  get,
  getOr,
  isEmpty,
  map,
  filter,
  keyBy,
  intersection,
  size,
  includes,
  xor
} from 'lodash/fp';
import {
  Button,
  ButtonToolbar,
  ControlLabel,
  Form,
  FormControl,
  Icon,
  RadioGroup,
  Radio,
  SelectPicker,
  Schema
} from 'rsuite';
import { toastr } from 'react-redux-toastr';
import styled from 'styled-components';
import { getMainSettingsData } from '../../../redux/Settings/selectors/main';
import { getIsSaving } from '../../../redux/Reservation/selectors/routes/create';
import { getIsSaving as getIsUpdating } from '../../../redux/Reservation/selectors/routes/edit';
import { getDefaultEvent as getReservationDefaultEvent } from '../../../redux/Reservation/selectors/components/form';
import {
  getEventForId,
  getIsFetching as getIsFetchingAvailableEvents
} from '../../../redux/Reservation/selectors/available-events';
import {
  createReservation,
  fetchReservationAvailableEvent,
  updateReservation
} from '../../../redux/Reservation/sagas/routines';
import { CompressFormGroup } from '../../widgets/FormElements';
import ProgramSelection from '../ProgramsSelection';
import EventsSelection from '../EventsSelection';
import { reservationsCreatePath } from '../../../routes/Paths';
import history from '../../../routes/history';
import OutreachColors from '../../../constants/reservation/OutreachColors';
import * as Status from '../../../constants/reservation/Status';
import { GuestType } from '../../../interfaces/guest';
import {
  ReservationProgramType,
  ReservationType
} from '../../../interfaces/reservation';
import { parseInt } from '../../../helpers/utils';
import { hasViewAccess, viewAccess } from '../../../helpers/permission';
import guestAddressLine from '../../../helpers/guestAddressLine';
import { VIEW_ADDRESS } from '../../../constants/permissions/guest';
import { getUserPermissionsAsMap } from '../../../redux/selectors/permission';
import { hasPortalFeature } from '../../../helpers/config';
import EventColor from './EventColor';
import EventDate from './EventDate';
import EventName from './EventName';
import FormErrorMessage from './FormErrorMessage';
import NotifyGuestConfirmationModal from '../NotifyGuestConfirmationModal';
import CancelReservationConfirmationModal from '../CancelReservationConfirmationModal';
import { DefaultValues, StatusOptions } from './settings';
import AccessControl from '../../AccessControl';
import { useSmtpAccounts } from '../../../hooks/useSmtpAccounts';

const StyledControlLabel = styled(ControlLabel)`
  font-size: 12px !important;
`;

const GuestLabel = styled.div`
  float: left;
  width: 200px;
  padding: 10px;
  font-size: 15px;
  margin-top: -7px;
`;

const { StringType, NumberType, ArrayType } = Schema.Types;

const contactNumberValidation = () =>
  StringType().maxLength(20, 'Maximum of 20 characters');

const programsToOptions = map(({ id: value, name: label }) => ({
  value,
  label
}));

const filterEligiblePrograms = (programs, eventPrograms) => {
  const eventProgramsById = keyBy('id', eventPrograms);
  return filter(
    programId =>
      has(programId, eventProgramsById) &&
      get('isGuestEligible', eventProgramsById[programId]),
    programs
  );
};

const ReservationForm = ({ onReset, data, guest, reservationPrograms }) => {
  const dispatch = useDispatch();
  const formRef = useRef();
  const { search: queryString } = useLocation();
  const { redirectPath = '' } = useMemo(
    () => qs.parse(queryString, { ignoreQueryPrefix: true }),
    [queryString]
  );

  const [formValue, setFormValue] = useState(DefaultValues);
  const [formError, setFormError] = useState({});
  const [newEmailInput, setNewEmailInput] = useState('');
  const [showNotifyGuestDialog, setShowNotifyGuestDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const settings = useSelector(getMainSettingsData);
  const isSaving = useSelector(getIsSaving);
  const isUpdating = useSelector(getIsUpdating);
  const isFetchingEvents = useSelector(getIsFetchingAvailableEvents);
  const defaultEvent = useSelector(getReservationDefaultEvent);
  const permissions = useSelector(getUserPermissionsAsMap);
  const { hasActive: hasActiveSmtpAccount } = useSmtpAccounts();
  const guestId = get('guestId', formValue);
  const hasGuest = !!guestId;
  const programs = get('programs', formValue);
  const homeless = guestId
    ? get('homeless', guest)
    : getOr(0, 'homeless', formValue);
  const guestAddress = guestAddressLine(guest, true);
  const currentDate = moment().format('YYYY-MM-DD');
  const outreach = get('outreach', data);
  const reservationDate = get('requestTime', data);
  const isPastReservation =
    reservationDate && moment(reservationDate).isBefore(currentDate);
  const defaultEmail = get('email', guest) || get('email', formValue);

  const showAddress = settings.reservationShowAddress === '1';
  const showCounty = settings.reservationShowCounty === '1';
  const showHouseholdTotal = settings.reservationShowHouseholdTotal === '1';
  const showEmail = settings.reservationShowEmail === '1';
  const showHomePhone = settings.reservationShowHomePhone === '1';
  const showWorkPhone = settings.reservationShowWorkPhone === '1';
  const showCellPhone = settings.reservationShowCellPhone === '1';
  const showNote =
    settings.reservationShowNote === '1' ||
    get('status', formValue) === Status.CANCELED;

  const isStreetRequired =
    showAddress && settings.reservationRequireStreetAddress === '1';
  const isCityRequired = showAddress && settings.reservationRequireCity === '1';
  const isStateRequired =
    showAddress && settings.reservationRequireState === '1';
  const isZipCodeRequired =
    showAddress && settings.reservationRequireZip === '1';
  const isCountyRequired =
    showCounty && settings.reservationRequireCounty === '1';
  const isHouseholdTotalRequired =
    showHouseholdTotal && settings.reservationRequireHouseholdTotal === '1';
  const isEmailRequired = showEmail && settings.reservationRequireEmail === '1';
  const isHomePhoneRequred =
    showHomePhone && settings.reservationRequireHomePhone === '1';
  const isWorkPhoneRequred =
    showWorkPhone && settings.reservationRequireWorkPhone === '1';
  const isCellPhoneRequred =
    showCellPhone && settings.reservationRequireCellPhone === '1';

  const isCreate = !get('id', data);
  const isCancelling =
    !isCreate &&
    get('status', formValue) === Status.CANCELED &&
    get('status', data) !== Status.CANCELED;
  const showNotifyGuest = useMemo(
    () => {
      if (!hasPortalFeature) return false;

      const status = get('status', formValue);
      const isChangeStatus = get('status', data) !== status;

      // Notify when creating APPROVED status
      if (isCreate && status === Status.ACTIVE) return true;

      // Notify when updating to APPROVED, CANCELED and REJECTED
      if (
        isChangeStatus &&
        includes(status, [Status.ACTIVE, Status.REJECTED, Status.CANCELED])
      )
        return true;

      const hasChangeToNotify =
        get('requestTime', data) !== get('requestTime', formValue) ||
        xor(get('programs', data), get('programs', formValue)).length > 0;

      // Notify when Schedule or Programs has changed for ACTIVE and CONFIRMED
      return (
        hasChangeToNotify && includes(status, [Status.ACTIVE, Status.CONFIRMED])
      );
    },
    [hasPortalFeature, showEmail, data, formValue]
  );

  const outreachId = formValue.outreachId
    ? parseInt(formValue.outreachId)
    : null;

  const reservationStatusOptions = useMemo(
    () =>
      formValue.id
        ? StatusOptions
        : filter(
            ({ value }) => includes(value, [Status.PENDING, Status.ACTIVE]),
            StatusOptions
          ),
    [formValue]
  );

  const selectedEvent =
    useSelector(state => getEventForId(state, outreachId)) || defaultEvent;
  const eventPrograms = get('trackingMethods', selectedEvent);

  const includeDefaultEvent = useMemo(
    () => {
      if (!defaultEvent) return false;
      const defaultEventPrograms = map('id', defaultEvent.trackingMethods);
      return size(intersection(programs, defaultEventPrograms)) > 0;
    },
    [programs, defaultEvent]
  );

  const handleOnChangeHomeless = value => {
    setFormValue({ ...formValue, homeless: value });
  };

  const handleSelectTimeSlot = value => {
    setFormValue({ ...formValue, ...value });
  };

  const handleFormChange = value => {
    const unsetSelectedEventAndTime =
      data.id && !size(intersection(value.programs, data.programs));

    if (unsetSelectedEventAndTime) {
      setFormValue({ ...value, outreachId: null, requestTime: '' });
    } else {
      setFormValue(value);
    }
  };

  const handleUnlink = () => {
    const newValue = !get('id', data)
      ? {
          ...formValue,
          guestId: null,
          firstName: '',
          lastName: '',
          homeless: 0,
          streetAddress: '',
          apartment: '',
          city: '',
          state: '',
          zipCode: '',
          email: '',
          homePhone: '',
          workPhone: '',
          cellPhone: '',
          householdTotal: ''
        }
      : { ...formValue, guestId: null };
    setFormValue(newValue);
  };

  const handleCancel = () => {
    history.push(reservationsCreatePath);
  };

  const handleReset = () => {
    setFormValue({ ...formValue, ...data });
    onReset();
  };

  const submitForm = (
    notifyGuest = false,
    newEmail = '',
    reason = cancellationReason
  ) => {
    const { id, ...rawBody } = formValue;
    const eligiblePrograms = filterEligiblePrograms(programs, eventPrograms);
    const body = {
      ...rawBody,
      programs: eligiblePrograms,
      email: newEmail || get('email', formValue),
      notifyGuest
    };
    if (isCancelling) body.cancellationReason = reason;
    if (id) dispatch(updateReservation({ id, body }));
    else dispatch(createReservation({ body, redirectPath }));
  };

  const handleSubmit = () => {
    if (!formRef.current.check()) {
      toastr.error('Oops!', 'Unable to save. Please check form error.');
      return;
    }

    if (isCancelling) {
      setShowCancelDialog(true);
    } else if (showNotifyGuest && hasActiveSmtpAccount) {
      setShowNotifyGuestDialog(true);
    } else {
      submitForm();
    }
  };

  const handleCloseCancelDialog = () => {
    setShowCancelDialog(false);
  };

  const handleConfirmCancelDialog = reason => {
    setCancellationReason(reason);
    setShowCancelDialog(false);

    if (showNotifyGuest && hasActiveSmtpAccount) {
      setShowNotifyGuestDialog(true);
      return;
    }
    submitForm(false, '', reason);
  };

  const handleFormCheck = errors => {
    setFormError(errors);
  };

  const handleConfirmNotifyGuestDialog = () => {
    setShowNotifyGuestDialog(false);
    const notifyGuest = true;
    submitForm(notifyGuest, newEmailInput || defaultEmail);
  };

  const handleSkipNotifyGuestDialog = () => {
    setShowNotifyGuestDialog(false);
    const notifyGuest = false;
    submitForm(notifyGuest);
  };

  const handleCancelNotifyGuestDialog = () => {
    setShowNotifyGuestDialog(false);
  };

  const handleOnChangeEmail = value => {
    setNewEmailInput(value);
  };

  useEffect(
    () => {
      if (!isEmpty(data)) {
        setFormValue({ ...formValue, ...data });
      }
    },
    [data]
  );

  useEffect(
    () => {
      const defaultOutreachId = get('id', outreach);
      if (guestId && defaultOutreachId) {
        dispatch(fetchReservationAvailableEvent(defaultOutreachId));
      }
    },
    [guestId, outreach]
  );

  const addRequiredRuleBasedOnConfig = (validation, isRequired) => {
    if (isRequired) return validation.isRequired('Required field');
    return validation;
  };

  const formModel = () => {
    const addressValidation =
      showAddress && !homeless && !guestId
        ? {
            streetAddress: addRequiredRuleBasedOnConfig(
              StringType(),
              isStreetRequired
            ),
            apartment: StringType(),
            city: addRequiredRuleBasedOnConfig(StringType(), isCityRequired),
            state: addRequiredRuleBasedOnConfig(StringType(), isStateRequired),
            zipCode: addRequiredRuleBasedOnConfig(
              StringType().pattern(
                /^[a-zA-Z0-9 ]{1,9}$/,
                'Only accepts numbers, letters and spaces - maximum of 9 characters'
              ),
              isZipCodeRequired
            ),
            county: addRequiredRuleBasedOnConfig(StringType(), isCountyRequired)
          }
        : {};

    return Schema.Model({
      id: NumberType(),
      guestId: NumberType(),
      firstName: StringType().isRequired('Required field'),
      lastName: StringType().isRequired('Required field'),
      homeless: NumberType().isRequired('Required field'),
      ...addressValidation,
      householdTotal: addRequiredRuleBasedOnConfig(
        NumberType('Invalid numeric value'),
        isHouseholdTotalRequired
      ),
      email: addRequiredRuleBasedOnConfig(
        StringType().isEmail('Invalid Email'),
        isEmailRequired
      ),
      homePhone: addRequiredRuleBasedOnConfig(
        contactNumberValidation(),
        isHomePhoneRequred
      ),
      workPhone: addRequiredRuleBasedOnConfig(
        contactNumberValidation(),
        isWorkPhoneRequred
      ),
      cellPhone: addRequiredRuleBasedOnConfig(
        contactNumberValidation(),
        isCellPhoneRequred
      ),
      programs: ArrayType()
        .of(NumberType())
        .isRequired('Required field'),
      outreachId: NumberType(),
      requestTime: StringType().isRequired('Required field'),
      status: NumberType().isRequired('Required field'),
      note: StringType()
    });
  };

  const inputPlaceholder = name =>
    guestId && !get(name, formValue) ? 'N/A' : '';

  const hasError = field => has(field, formError);
  const programsOptions = programsToOptions(reservationPrograms);

  return (
    <Form
      ref={formRef}
      layout="horizontal"
      formValue={formValue}
      model={formModel()}
      onChange={handleFormChange}
      onCheck={handleFormCheck}
      style={{ overflow: 'hidden' }}
    >
      {!isEmpty(formError) && <FormErrorMessage formError={formError} />}
      <CompressFormGroup className={hasError('firstName') && 'error'}>
        <StyledControlLabel>First Name *</StyledControlLabel>
        {hasGuest ? (
          <GuestLabel>{guest.firstname}</GuestLabel>
        ) : (
          <FormControl name="firstName" errorMessage="" />
        )}
      </CompressFormGroup>
      <CompressFormGroup className={hasError('lastName') && 'error'}>
        <StyledControlLabel>Last Name *</StyledControlLabel>
        {hasGuest ? (
          <GuestLabel>{guest.lastname}</GuestLabel>
        ) : (
          <FormControl name="lastName" errorMessage="" />
        )}
      </CompressFormGroup>
      {hasGuest && (
        <CompressFormGroup>
          <StyledControlLabel>Guest ID</StyledControlLabel>
          <GuestLabel>
            {guestId}
            &nbsp;
            <Button
              size="xs"
              appearance="ghost"
              onClick={handleUnlink}
              disabled={isSaving || isUpdating}
            >
              <Icon icon="unlink" /> Unlink
            </Button>
          </GuestLabel>
        </CompressFormGroup>
      )}
      <CompressFormGroup controlId="homeless">
        <StyledControlLabel style={{ whiteSpace: 'break-spaces' }}>
          Has Residence?
        </StyledControlLabel>
        <RadioGroup
          name="homeless"
          value={homeless}
          onChange={handleOnChangeHomeless}
          inline
        >
          <Radio disabled={hasGuest} value={0}>
            Yes
          </Radio>
          <Radio disabled={hasGuest} value={1}>
            No
          </Radio>
        </RadioGroup>
      </CompressFormGroup>
      {!homeless && (
        <Fragment>
          {!hasGuest ? (
            showAddress && (
              <Fragment>
                {(isStreetRequired ||
                  hasViewAccess(VIEW_ADDRESS, permissions)) && (
                  <Fragment>
                    <CompressFormGroup
                      className={hasError('streetAddress') && 'error'}
                    >
                      <StyledControlLabel>
                        Street Address {isStreetRequired && '*'}
                      </StyledControlLabel>
                      <FormControl name="streetAddress" errorMessage="" />
                    </CompressFormGroup>
                    <CompressFormGroup
                      className={hasError('apartment') && 'error'}
                    >
                      <StyledControlLabel>Apartment/Suite</StyledControlLabel>
                      <FormControl
                        name="apartment"
                        errorMessage=""
                        placeholder={inputPlaceholder('apartment')}
                      />
                    </CompressFormGroup>
                  </Fragment>
                )}

                <CompressFormGroup className={hasError('city') && 'error'}>
                  <StyledControlLabel>
                    City/Town {isCityRequired && '*'}
                  </StyledControlLabel>
                  <FormControl name="city" errorMessage="" />
                </CompressFormGroup>
                <CompressFormGroup className={hasError('state') && 'error'}>
                  <StyledControlLabel>
                    State/Province {isStateRequired && '*'}
                  </StyledControlLabel>
                  <FormControl name="state" errorMessage="" />
                </CompressFormGroup>
                <CompressFormGroup className={hasError('zipCode') && 'error'}>
                  <StyledControlLabel>
                    Zip/Postal Code {isZipCodeRequired && '*'}
                  </StyledControlLabel>
                  <FormControl name="zipCode" errorMessage="" />
                </CompressFormGroup>
                {showCounty && (
                  <CompressFormGroup className={hasError('county') && 'error'}>
                    <StyledControlLabel>
                      County {isCountyRequired && '*'}
                    </StyledControlLabel>
                    <FormControl name="county" errorMessage="" />
                  </CompressFormGroup>
                )}
              </Fragment>
            )
          ) : (
            <CompressFormGroup>
              <StyledControlLabel>Complete Address</StyledControlLabel>
              <GuestLabel style={{ width: 400 }}>
                <AccessControl allowOnlyFor={viewAccess(VIEW_ADDRESS)}>
                  {guestAddress.line1}
                  {`, `}
                </AccessControl>
                {guestAddress.line2}
              </GuestLabel>
            </CompressFormGroup>
          )}
        </Fragment>
      )}
      {showHouseholdTotal && (
        <CompressFormGroup className={hasError('householdTotal') && 'error'}>
          <StyledControlLabel>
            Total Individuals {!hasGuest && isHouseholdTotalRequired && '*'}
          </StyledControlLabel>
          {hasGuest ? (
            <GuestLabel>{guest.household_total}</GuestLabel>
          ) : (
            <FormControl name="householdTotal" errorMessage="" />
          )}
        </CompressFormGroup>
      )}
      {showEmail && (
        <CompressFormGroup className={hasError('email') && 'error'}>
          <StyledControlLabel>
            Email {!hasGuest && isEmailRequired && '*'}
          </StyledControlLabel>
          {hasGuest ? (
            <GuestLabel>{guest.email || 'N/A'}</GuestLabel>
          ) : (
            <FormControl name="email" errorMessage="" />
          )}
        </CompressFormGroup>
      )}
      {!hasGuest ? (
        <Fragment>
          {showHomePhone && (
            <CompressFormGroup className={hasError('homePhone') && 'error'}>
              <StyledControlLabel>
                Home Phone {isHomePhoneRequred && '*'}
              </StyledControlLabel>
              <FormControl name="homePhone" errorMessage="" />
            </CompressFormGroup>
          )}
          {showWorkPhone && (
            <CompressFormGroup className={hasError('workPhone') && 'error'}>
              <StyledControlLabel>
                Work Phone {isWorkPhoneRequred && '*'}
              </StyledControlLabel>
              <FormControl name="workPhone" errorMessage="" />
            </CompressFormGroup>
          )}
          {showCellPhone && (
            <CompressFormGroup className={hasError('cellPhone') && 'error'}>
              <StyledControlLabel>
                Cell Phone {isCellPhoneRequred && '*'}
              </StyledControlLabel>
              <FormControl name="cellPhone" errorMessage="" />
            </CompressFormGroup>
          )}
        </Fragment>
      ) : (
        <Fragment>
          {showHomePhone && (
            <CompressFormGroup>
              <StyledControlLabel>Home Phone</StyledControlLabel>
              <GuestLabel>{get('phone', guest) || 'N/A'}</GuestLabel>
            </CompressFormGroup>
          )}
          {showWorkPhone && (
            <CompressFormGroup>
              <StyledControlLabel>Work Phone</StyledControlLabel>
              <GuestLabel>{get('workphone', guest) || 'N/A'}</GuestLabel>
            </CompressFormGroup>
          )}
          {showCellPhone && (
            <CompressFormGroup>
              <StyledControlLabel>Cell Phone</StyledControlLabel>
              <GuestLabel>{get('cellphone', guest) || 'N/A'}</GuestLabel>
            </CompressFormGroup>
          )}
        </Fragment>
      )}

      <ProgramSelection
        defaultValue={programs}
        disabled={isPastReservation || isFetchingEvents}
        hasError={hasError('programs')}
        guestId={guestId}
        additionalOptions={programsOptions}
      />
      {isPastReservation ? (
        <CompressFormGroup>
          <StyledControlLabel>Time Slot *</StyledControlLabel>
          {reservationDate && (
            <div style={{ float: 'left', marginBottom: 20 }}>
              <h5>{moment(reservationDate).format('dddd')}</h5>
              <EventDate>{moment(reservationDate).format('LLL')}</EventDate>
              <EventName>
                <EventColor color={OutreachColors[outreach.color]} />{' '}
                {outreach.name}
              </EventName>
            </div>
          )}
        </CompressFormGroup>
      ) : (
        <CompressFormGroup className={hasError('requestTime') && 'error'}>
          <StyledControlLabel>Select Available Time *</StyledControlLabel>
          {isEmpty(programs) ? (
            <div style={{ marginTop: 6, fontSize: 14 }}>
              Select &quot;Help Desired&quot; above to show available time
            </div>
          ) : (
            <FormControl
              name="requestTime"
              guestId={guestId}
              selectedEventId={outreachId}
              selectedPrograms={programs}
              accepter={EventsSelection}
              onSelect={handleSelectTimeSlot}
              defaultEvent={includeDefaultEvent ? defaultEvent : null}
              errorMessage=""
              style={{ width: 766 }}
            />
          )}
        </CompressFormGroup>
      )}
      <CompressFormGroup className={hasError('status') && 'error'}>
        <StyledControlLabel>Status *</StyledControlLabel>
        <FormControl
          name="status"
          accepter={SelectPicker}
          data={reservationStatusOptions}
          style={{ width: 300 }}
          errorMessage=""
        />
      </CompressFormGroup>
      {showNote && (
        <CompressFormGroup className={hasError('note') && 'error'}>
          <StyledControlLabel>Note</StyledControlLabel>
          <FormControl name="note" componentClass="textarea" errorMessage="" />
        </CompressFormGroup>
      )}

      <ButtonToolbar style={{ textAlign: 'right' }}>
        {formValue.id ? (
          <Button onClick={handleReset} disabled={isSaving || isUpdating}>
            <Icon icon="refresh2" /> Reset
          </Button>
        ) : (
          <Button onClick={handleCancel} disabled={isSaving || isUpdating}>
            <Icon icon="close-circle" /> Cancel
          </Button>
        )}
        <Button
          appearance="primary"
          onClick={handleSubmit}
          disabled={isSaving || isUpdating}
        >
          <Icon icon="save" /> {isSaving || isUpdating ? 'Saving...' : 'Save'}
        </Button>
      </ButtonToolbar>
      {showNotifyGuest &&
        hasActiveSmtpAccount && (
          <NotifyGuestConfirmationModal
            show={showNotifyGuestDialog}
            email={defaultEmail}
            showEmailField={!isCreate || hasGuest}
            onConfirm={handleConfirmNotifyGuestDialog}
            onSkip={handleSkipNotifyGuestDialog}
            onCancel={handleCancelNotifyGuestDialog}
            onChangeEmail={handleOnChangeEmail}
          />
        )}
      <CancelReservationConfirmationModal
        show={showCancelDialog}
        message="Are you sure you want to cancel this reservation? Saving will set its status to Canceled."
        onCancel={handleCloseCancelDialog}
        onConfirm={handleConfirmCancelDialog}
      />
    </Form>
  );
};

ReservationForm.propTypes = {
  onReset: PropTypes.func,
  data: ReservationType,
  guest: GuestType,
  reservationPrograms: PropTypes.arrayOf(ReservationProgramType)
};

ReservationForm.defaultProps = {
  onReset: () => {},
  data: {},
  guest: {},
  reservationPrograms: []
};

export default ReservationForm;
